import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  List,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  ListItemButton,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { YouTubeSubscription } from '../../types/api.types';
import { buildCheckoutUrl } from '../../utils/billing';
import {
  FREE_GROUP_CHANNEL_LIMIT,
  FREE_GROUP_LIMIT,
  PRO_GROUP_CHANNEL_LIMIT,
  getChannelLimit,
} from '../../utils/limits';
import PaywallModal from '../common/PaywallModal';

interface ChannelSelectionModalProps {
  open: boolean;
  onClose: () => void;
  subscriptions: YouTubeSubscription[];
  mode: 'create' | 'add';
  // For create mode
  onCreateGroup?: (name: string, channelIds: string[]) => Promise<void>;
  existingGroupNames?: string[];
  // For add mode
  groupName?: string;
  onAddChannels?: (groupName: string, channelIds: string[]) => Promise<void>;
  existingChannelIds?: string[];
  userHasPro: boolean;
  userBillingId: string;
}

export const ChannelSelectionModal: React.FC<ChannelSelectionModalProps> = ({
  open,
  onClose,
  subscriptions,
  mode,
  onCreateGroup,
  existingGroupNames = [],
  groupName = '',
  onAddChannels,
  existingChannelIds = [],
  userHasPro = false,
  userBillingId,
}) => {
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const channelLimit = getChannelLimit(userHasPro);
  const existingCount = existingChannelIds.length;
  const remainingSlots = Math.max(channelLimit - existingCount, 0);

  // Filter out already-added channels when in 'add' mode
  const availableSubscriptions = useMemo(() => {
    if (mode === 'add') {
      return subscriptions.filter(sub => !existingChannelIds.includes(sub.channelId));
    }
    return subscriptions;
  }, [mode, subscriptions, existingChannelIds]);

  // Filter subscriptions based on search query
  const filteredSubscriptions = useMemo(() => {
    return availableSubscriptions.filter(sub => sub.channel.title.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [availableSubscriptions, searchQuery]);

  const handleToggleChannel = (channelId: string) => {
    setSelectedChannels(prev => {
      if (prev.includes(channelId)) {
        return prev.filter(id => id !== channelId);
      }

      if (prev.length >= remainingSlots) {
        if (!userHasPro) {
          setError(
            `Free plan allows up to ${FREE_GROUP_CHANNEL_LIMIT} channels per group. Upgrade to save up to ${PRO_GROUP_CHANNEL_LIMIT} channels per group.`,
          );
          setPaywallOpen(true);
        } else {
          setError(`Groups can include up to ${PRO_GROUP_CHANNEL_LIMIT} channels.`);
        }
        return prev;
      }

      setError(null);
      return [...prev, channelId];
    });
  };

  const handleSelectAll = () => {
    if (selectedChannels.length === filteredSubscriptions.length) {
      setSelectedChannels([]);
    } else {
      setSelectedChannels(filteredSubscriptions.slice(0, remainingSlots).map(sub => sub.channelId));
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (mode === 'create') {
      if (!newGroupName.trim()) {
        setError('Please enter a group name');
        return;
      }

      if (existingGroupNames.includes(newGroupName.trim())) {
        setError('A group with this name already exists');
        return;
      }
    }

    if (selectedChannels.length === 0) {
      setError('Please select at least one channel');
      return;
    }

    if (selectedChannels.length > remainingSlots) {
      setError(
        userHasPro
          ? `Groups can include up to ${PRO_GROUP_CHANNEL_LIMIT} channels.`
          : `Free plan allows up to ${FREE_GROUP_CHANNEL_LIMIT} channels per group. Upgrade to save up to ${PRO_GROUP_CHANNEL_LIMIT} channels per group.`,
      );
      if (!userHasPro) {
        setPaywallOpen(true);
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (mode === 'create' && onCreateGroup) {
        await onCreateGroup(newGroupName.trim(), selectedChannels);
      } else if (mode === 'add' && onAddChannels) {
        await onAddChannels(groupName, selectedChannels);
      }
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${mode === 'create' ? 'create group' : 'add channels'}`);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewGroupName('');
    setSelectedChannels([]);
    setSearchQuery('');
    setError(null);
    setLoading(false);
    onClose();
  };

  // Dynamic text based on mode
  const dialogTitle = mode === 'create' ? 'Create New Group' : `Add Channels to "${groupName}"`;
  const submitButtonText =
    mode === 'create' ? (loading ? 'Creating...' : 'Create Group') : loading ? 'Adding...' : 'Add Channels';
  const noChannelsMessage =
    mode === 'add' ? 'All available channels are already in this group' : 'No subscriptions available';

  const checkoutUrl = buildCheckoutUrl(userBillingId);

  const isCreateGroupWithoutProAndLimitReached =
    mode === 'create' && !userHasPro && existingGroupNames.length >= FREE_GROUP_LIMIT;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}

          {mode === 'create' && (
            <TextField
              label="Group Name"
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              fullWidth
              required
              placeholder="e.g., Tech Channels, Gaming, Educational"
              error={!!error && error.includes('name')}
              disabled={loading}
            />
          )}

          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1">
                {mode === 'add' && availableSubscriptions.length === 0
                  ? 'No Available Channels'
                  : `Select Channels (${selectedChannels.length} selected)`}
              </Typography>
              {filteredSubscriptions.length > 0 && (
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  disabled={loading || isCreateGroupWithoutProAndLimitReached}
                >
                  {selectedChannels.length === filteredSubscriptions.length && filteredSubscriptions.length > 0
                    ? 'Deselect All'
                    : 'Select All'}
                </Button>
              )}
            </Box>
            {!userHasPro &&
              (selectedChannels.length >= remainingSlots || existingCount >= FREE_GROUP_CHANNEL_LIMIT) && (
                <Alert severity="warning" style={{ marginBottom: '8px' }}>
                  <Link component="button" type="button" onClick={() => setPaywallOpen(true)}>
                    Upgrade
                  </Link>{' '}
                  to add more than {FREE_GROUP_CHANNEL_LIMIT} channels to a group.
                </Alert>
              )}
            {userHasPro && existingCount >= PRO_GROUP_CHANNEL_LIMIT && (
              <Alert severity="info" style={{ marginBottom: '8px' }}>
                Groups can include up to {PRO_GROUP_CHANNEL_LIMIT} channels.
              </Alert>
            )}
            {isCreateGroupWithoutProAndLimitReached && (
              <Alert severity="warning" style={{ marginBottom: '8px' }}>
                <Link component="button" type="button" onClick={() => setPaywallOpen(true)}>
                  Upgrade
                </Link>{' '}
                to add more than {FREE_GROUP_LIMIT} groups.
              </Alert>
            )}

            {availableSubscriptions.length > 0 && (
              <TextField
                placeholder="Search channels..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                fullWidth
                size="small"
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
            )}

            <Box
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {availableSubscriptions.length === 0 ? (
                <Box p={3} textAlign="center">
                  <Typography color="text.secondary">{noChannelsMessage}</Typography>
                </Box>
              ) : filteredSubscriptions.length === 0 ? (
                <Box p={3} textAlign="center">
                  <Typography color="text.secondary">No channels found matching "{searchQuery}"</Typography>
                </Box>
              ) : (
                <List dense>
                  {filteredSubscriptions.map(subscription => {
                    const isSelected = selectedChannels.includes(subscription.channelId);
                    const proChannelsLimitReached =
                      selectedChannels.length >= remainingSlots || existingCount >= channelLimit;
                    const proGroupsLimitReached = !userHasPro && existingGroupNames.length >= FREE_GROUP_LIMIT;
                    return (
                      <ListItemButton
                        key={subscription.id}
                        onClick={() => handleToggleChannel(subscription.channelId)}
                        disabled={loading || (proChannelsLimitReached && !isSelected) || proGroupsLimitReached}
                      >
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          disabled={(proChannelsLimitReached && !isSelected) || proGroupsLimitReached}
                        />
                        <ListItemAvatar>
                          <Avatar
                            src={subscription.channel.thumbnailUrl}
                            alt={subscription.channel.title}
                            sx={{ width: 40, height: 40 }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={subscription.channel.title}
                          secondary={
                            subscription.channel.subscriberCount
                              ? `${subscription.channel.subscriberCount.toLocaleString()} subscribers`
                              : subscription.channel.description
                          }
                          primaryTypographyProps={{ noWrap: true }}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || selectedChannels.length === 0 || (mode === 'create' && !newGroupName.trim())}
          data-cy={mode === 'create' ? 'submit-create-group' : 'submit-add-channels'}
        >
          {submitButtonText}
        </Button>
      </DialogActions>
      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} checkoutUrl={checkoutUrl} />
    </Dialog>
  );
};

// Wrapper component for backward compatibility with existing CreateGroupModal usage
export const CreateGroupModal: React.FC<{
  open: boolean;
  onClose: () => void;
  subscriptions: YouTubeSubscription[];
  onCreateGroup: (name: string, channelIds: string[]) => Promise<void>;
  existingGroupNames: string[];
  userHasPro: boolean;
  userBillingId: string;
}> = props => {
  return (
    <ChannelSelectionModal
      {...props}
      mode="create"
      onAddChannels={undefined}
      existingChannelIds={[]}
      userBillingId={props.userBillingId}
    />
  );
};
